/** Konsistenter Produkt-ID-Vergleich (number vs string nach Storage-Migration). */
import { formatSequenceDigits } from './sequenceDigits';

export function sameProductId(a, b) {
  if (a == null || b === '' || b == null || a === '') return false;
  return String(a) === String(b);
}

/** Lot-ID-Vergleich (number vs string). */
export function sameLotId(a, b) {
  if (a == null || a === '' || b == null || b === '') return false;
  return String(a) === String(b);
}

/**
 * Lot match for cart lines: empty/missing on both sides counts as equal
 * (product without attached batch/lot).
 */
export function sameOptionalLotId(a, b) {
  const aEmpty = a == null || a === '';
  const bEmpty = b == null || b === '';
  if (aEmpty && bEmpty) return true;
  if (aEmpty || bEmpty) return false;
  return String(a) === String(b);
}

/** Katalog-Identität: id bevorzugt, sonst productId. */
export function canonicalProductId(product) {
  if (!product || typeof product !== 'object') return null;
  const id = product.id ?? product.productId;
  if (id == null || id === '') return null;
  return id;
}

/**
 * Eindeutige ganzzahlige Produkt-ID (kein Float, kein timestamp_index).
 * @param {Array} existingProducts
 * @param {{ offset?: number }} [options]
 * @returns {number}
 */
export function generateProductId(existingProducts = [], { offset = 0 } = {}) {
  const list = Array.isArray(existingProducts) ? existingProducts : [];
  let candidate = Date.now() + (Number.isFinite(offset) ? Math.max(0, Math.floor(offset)) : 0);

  const taken = (id) =>
    list.some((p) => sameProductId(canonicalProductId(p), id));

  while (taken(candidate)) {
    candidate += 1;
  }

  return candidate;
}

/**
 * Katalog-Produkt anhand SKU finden (SKU ist Lookup-Key, nicht die Identität).
 * PROD1 und PROD001 gelten als dieselbe SKU.
 * @param {unknown} sku
 * @param {Array} products
 * @returns {object|null}
 */
export function findProductBySku(sku, products = []) {
  const key = normalizeSkuKey(sku);
  if (!key || !Array.isArray(products)) return null;
  return products.find((p) => normalizeSkuKey(p?.sku) === key) || null;
}

function normalizeSkuKey(sku) {
  const raw = String(sku || '').trim();
  if (!raw) return '';
  const m = raw.match(/^PROD(\d+)$/i);
  if (m) {
    return `prod${formatSequenceDigits(parseInt(m[1], 10))}`;
  }
  return raw.toLowerCase();
}

/**
 * Rohwert (echte id oder SKU) → kanonische Produkt-ID.
 * SKU wird nie als ID zurückgegeben, nur die id des gefundenen Produkts.
 * @param {unknown} rawIdOrSku
 * @param {Array} products
 * @returns {string|number|null}
 */
export function resolveProductId(rawIdOrSku, products = []) {
  if (rawIdOrSku == null || rawIdOrSku === '') return null;
  const list = Array.isArray(products) ? products : [];

  const byId = list.find((p) => sameProductId(canonicalProductId(p), rawIdOrSku));
  if (byId) return canonicalProductId(byId);

  const bySku = findProductBySku(rawIdOrSku, list);
  if (bySku) return canonicalProductId(bySku);

  return null;
}
